import React, { useEffect, useState } from 'react'
import { useForm } from "react-hook-form"
import {useTranslation} from "react-i18next";

import Typography from '@mui/material/Typography';
import VisibilityOff from '@mui/icons-material/VisibilityOffSharp';
import { RemoveRedEye as RemoveRedEyeIcon } from '@vedmarket/ui-kit';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField'
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";

import {useGetIdFromRoute} from "../../../utils/getIdFromRoute";
import { useUpdatePasswordMutation } from "../../../api/auth";
import { Header, Notification, NOTIFICATION_TYPE } from '@vedmarket/ui-kit';
import {useLazyGetDictionaryLanguagesQuery} from "../../../api/localization";
import {changeAppLanguage, getLanguageCode} from "../../../config/i18";

import './../auth.scss'

enum RESET_PASSWORD_FORM {
  PASSWORD = 'password',
  PASSWORD_CONFIRM = 'passwordConfirm',
}

type ConfirmPasswordForm = {
  password: string
  passwordConfirm: string
}

const ResetPassword = () => {

  const [ isVisiblePassword, setIsVisiblePassword ] = useState(false);
  const [ isVisiblePasswordConfirm, setIsVisiblePasswordConfirm ] = useState(false);

  const [ errorEmail, setErrorEmail ] = useState('');
  const [ isSuccess, setIsSuccess ] = useState(false);

  const token = useGetIdFromRoute()

  const { t, i18n } = useTranslation()

  const [ localizationGetLanguagesRequest, { data: languages } ] = useLazyGetDictionaryLanguagesQuery();

  const { register, getValues, trigger, handleSubmit, formState: { errors, isValid } } = useForm<ConfirmPasswordForm>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })

  const selectedLanguage = languages?.find(({ code }) => getLanguageCode(code) === getLanguageCode(i18n.language))?.code;

  const validatePasswordConfirm = (value: string) => value === getValues(RESET_PASSWORD_FORM.PASSWORD) ? true: 'Пароли не совпадают'

  const [ updatePassword ] = useUpdatePasswordMutation()

  useEffect(() => {
    localizationGetLanguagesRequest('');
  }, [ localizationGetLanguagesRequest ]);

  const onSubmit = handleSubmit (async (values) => {

    try {

      const response: any = await updatePassword({ token, password: values.password})

      if (response?.error) {
        throw response?.error;
      } else {
        setIsSuccess(true)
      }
    } catch (error) {
      // @ts-ignore
      setErrorEmail(error.data.detail)
    }
  })


  return (
    <>
      <Header
        isEmpty
        languages={languages}
        user={{ lang: selectedLanguage || getLanguageCode(i18n.language) } as MeParamsOutput}
        changeLanguage={changeAppLanguage}
      />
      <form className='auth' onSubmit={onSubmit}>
        <Typography variant='h4' sx={{ fontWeight: 700, letterSpacing: '-1.15px' }}>
          {t('actions__text__passwordReset')}
        </Typography>
        <Typography variant='body1' sx={{ marginTop: 2, marginBottom: 3 }}>
          {t('common__text__passwordRequirements')}
        </Typography>
        <Card>
          <CardContent className='auth__card'>
            <TextField
              {...register(RESET_PASSWORD_FORM.PASSWORD, {
                required: t('common__text__isRequired'),
                minLength: { value: 8, message: `8 ${t('common__text__minLength')}` },
              })}
              error={Boolean(errors[RESET_PASSWORD_FORM.PASSWORD])}
              helperText={errors[RESET_PASSWORD_FORM.PASSWORD]?.message}
              variant='outlined'
              label={t('common__text__newPassword')}
              type={isVisiblePassword? 'text' : 'password'}
              InputProps={{
                endAdornment: <InputAdornment position='start' onClick={() => setIsVisiblePassword(!isVisiblePassword)} className='auth__input-adornment'>{isVisiblePassword ? <VisibilityOff/> : <RemoveRedEyeIcon/>}</InputAdornment>,
              }}
              sx={{
                marginTop: '18px',
              }}
              onBlur={async () => {
                if (getValues(RESET_PASSWORD_FORM.PASSWORD_CONFIRM)) {
                  await trigger([ RESET_PASSWORD_FORM.PASSWORD_CONFIRM ])
                }
              }}
            />
            <TextField
              {...register(RESET_PASSWORD_FORM.PASSWORD_CONFIRM, {
                required: t('common__text__isRequired'),
                minLength: { value: 8, message: `8 ${t('common__text__minLength')}` },
                validate: value => validatePasswordConfirm(value)
              })}
              error={Boolean(errors[RESET_PASSWORD_FORM.PASSWORD_CONFIRM])}
              helperText={errors[RESET_PASSWORD_FORM.PASSWORD_CONFIRM]?.message}
              variant='outlined'
              label={t('common__text__confirmNewPassword')}
              type={isVisiblePasswordConfirm? 'text' : 'password'}
              InputProps={{
                endAdornment: <InputAdornment position='start' onClick={() => setIsVisiblePasswordConfirm(!isVisiblePasswordConfirm)} className='auth__input-adornment'>{isVisiblePasswordConfirm ? <VisibilityOff/> : <RemoveRedEyeIcon/>}</InputAdornment>,
              }}
              sx={{
                marginTop: '22px',
              }}
            />
            <Button
              variant='contained'
              sx={{
                marginTop: '22px',
                marginBottom: '4px',
              }}
              type='submit'
              disabled={!isValid}
            >{t('actions__text__resetPassword')}</Button>
            {errorEmail && <Notification type={NOTIFICATION_TYPE.ERROR} title={errorEmail} />}
            {isSuccess && (
              <>
                <Notification type={NOTIFICATION_TYPE.SUCCESS} title={t('actions__message__updatePasswordSuccess')} sx={{
                  marginTop: '22px',
                }} />
                <Typography variant='body2'>
                  {t('auth__message__authNewPassword')}&nbsp;
                  <Link href='/login'>Авторизоваться</Link>
                      &nbsp;{t('auth__message__withNewPassword')}
                </Typography>
              </>
            )}
          </CardContent>
        </Card>
      </form>
    </>
  )
}

export default ResetPassword
